import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card, CardContent } from './ui/card';
import { useLanguage } from '../i18n';

const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { t } = useLanguage();

  // Wedding date: December 28, 2026 at 12:00
  const weddingDate = useMemo(() => new Date('2026-12-28T12:00:00'), []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = weddingDate - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [weddingDate]);

  const timeUnits = [
    { value: timeLeft.days, label: t('countdown.days'), labelSingular: t('countdown.day') },
    { value: timeLeft.hours, label: t('countdown.hours'), labelSingular: t('countdown.hour') },
    { value: timeLeft.minutes, label: t('countdown.minutes'), labelSingular: t('countdown.minute') },
    { value: timeLeft.seconds, label: t('countdown.seconds'), labelSingular: t('countdown.second') }
  ];

  return (
    <section 
      ref={sectionRef}
      className="py-28 md:py-40 relative overflow-hidden"
      id="countdown"
      style={{
        background: 'linear-gradient(180deg, hsl(0 0% 100%) 0%, hsl(0 73% 96%) 50%, hsl(0 0% 100%) 100%)'
      }}
    >
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-80 h-80 bg-lavender/8 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-blush/20 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-pale-blue/15 rounded-full blur-3xl" />
      
      {/* Floral pattern */}
      <div className="absolute inset-0 floral-pattern opacity-40" />
      
      {/* Floating sparkles */}
      <div className="absolute top-1/4 left-1/4 text-gold/30 text-xl animate-twinkle">✦</div>
      <div className="absolute top-1/3 right-1/4 text-gold/25 text-lg animate-twinkle delay-300">✦</div>
      <div className="absolute bottom-1/4 left-1/3 text-gold/30 text-base animate-twinkle delay-500">✦</div>
      <div className="absolute bottom-1/3 right-1/3 text-gold/25 text-xl animate-twinkle delay-200">✦</div>
      
      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-24 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="flex items-center justify-center mb-8">
            <svg className="w-14 h-14 text-lavender/50 animate-pulse-soft" viewBox="0 0 56 56" fill="currentColor">
              <path d="M28 8 C22 14 10 14 4 20 C10 24 22 24 28 34 C34 24 46 24 52 20 C46 14 34 14 28 8" />
              <circle cx="28" cy="20" r="3" fill="hsl(1 81% 35% / 0.6)" />
            </svg>
          </div>
          <p className="font-display text-sm uppercase tracking-[0.5em] text-gold mb-5">
            {t('countdown.eyebrow')}
          </p>
          <h2 className="font-script text-5xl md:text-6xl lg:text-7xl text-lavender mb-8">
            {t('countdown.title')}
          </h2>
          <p className="font-elegant text-xl md:text-2xl text-muted-foreground italic max-w-xl mx-auto">
            {t('countdown.subtitle')}
          </p>
        </div>

        {/* Countdown Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-8 max-w-5xl mx-auto">
          {timeUnits.map((unit, index) => (
            <Card 
              key={unit.label}
              className={`card-regency bg-card/95 backdrop-blur-sm border-2 border-lavender/20 shadow-romantic transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${200 + index * 100}ms` }}
            >
              <CardContent className="p-8 md:p-12 text-center relative">
                {/* Corner decorations */}
                <div className="absolute top-2 left-2 w-5 h-5 border-l-2 border-t-2 border-gold/30" />
                <div className="absolute bottom-2 right-2 w-5 h-5 border-r-2 border-b-2 border-gold/30" />
                
                <div className="relative">
                  <span className="font-display text-5xl md:text-7xl text-foreground block mb-4">
                    {String(unit.value).padStart(2, '0')}
                  </span>
                  <div className="w-10 h-px bg-gold/40 mx-auto mb-4" />
                  <span className="font-display text-xs md:text-sm uppercase tracking-[0.25em] text-muted-foreground">
                    {unit.value === 1 ? unit.labelSingular : unit.label}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Decorative divider */}
        <div className={`flex items-center justify-center gap-5 mt-24 transition-all duration-1000 delay-700 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}>
          <span className="w-28 h-px bg-gradient-to-r from-transparent via-lavender/30 to-transparent" />
          <div className="flex items-center gap-2">
            <span className="text-gold/50 text-lg">✿</span>
            <span className="text-lavender/50 text-xl">❦</span>
            <span className="text-gold/50 text-lg">✿</span>
          </div>
          <span className="w-28 h-px bg-gradient-to-l from-transparent via-lavender/30 to-transparent" />
        </div>
        
        {/* Quote */}
        <p className={`text-center font-elegant text-xl text-muted-foreground italic mt-10 transition-all duration-1000 delay-800 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}>
          &ldquo;{t('countdown.quote')}&rdquo;
        </p>
      </div>
    </section>
  );
};

export default CountdownTimer;
